import React, { useState, useEffect } from 'react';
import {
  Pane,
  toaster,
  Table,
  Link,
  IconButton,
  ChevronRightIcon,
  majorScale,
  Button,
  SearchInput,
  Badge,
  minorScale,
  ChevronDownIcon,
  ChevronUpIcon,
  FilterListIcon,
  Spinner,
  Tooltip,
  Heading,
} from 'evergreen-ui';
import { Link as ReactRouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import './index.scss';
import { api } from '../../services/api';
import { ExportTicketsDialog } from '../../components/dialogs';
import getStatusColor from '../../assets/scripts/getStatusColor';
import { searchParams } from '../../services/history';
import { stickyExpandOnScroll } from '../../assets/scripts/sticky';
import StatusFilter from './components/status-filter';
import ColumnsMenu from './components/columns-selector';
import TypeFilter from './components/type-filter';
import UserFilter from './components/user-filter';
import NoResults from '../../components/no-results';
import PaginationMenu from '../../components/pagination-menu';
import DateFilter from './components/date-filter';
import OrderByMenu from './components/order-by-selector';

const RequestsPage = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tickets, setTickets] = useState([]);
  const [metadata, setMetadata] = useState({});

  const [selectedTableColumns, setSelectedTableColumns] = useState(searchParams.get('columns') || 'title,status,created_at');
  const [orderBy, setOrderBy] = useState(searchParams.get('order_by') || 'created_at:desc');

  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'pending');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [filterCreator, setFilterCreator] = useState(searchParams.get('created_by') || 'all');
  const [filterCreatedRange, setFilterCreatedRange] = useState(searchParams.get('created_at') || 'all');

  async function fetchData() {
    try {
      setLoading(true);
      const page = searchParams.get('page') || 1;
      const itemsPerPage = searchParams.get('per_page') || 10;
      const { data } = await api.tickets.list({
        page,
        per_page: itemsPerPage,
        status: filterStatus,
        type: filterType,
        created_by: filterCreator,
        search: searchTerm,
        created_at: filterCreatedRange,
        order_by: orderBy,
      });
      setTickets(data.data);
      setMetadata(data.metadata);
      setLoading(false);
    } catch (error) {
      toaster.danger('There was an error while proccessing your request');
      setLoading(false);
    }
  }

  useEffect(() => {
    stickyExpandOnScroll('-24px');
  }, []);

  useEffect(() => {
    const event = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(event);
  }, [searchTerm, filterStatus, filterType, filterCreator, filterCreatedRange, orderBy]);

  const columnShown = (name) => selectedTableColumns.includes(name);

  const countActiveFilters = () => [filterStatus, filterType, filterCreator, filterCreatedRange].filter((v) => v !== 'all').length;

  return (
    <Pane>
      <Pane
        id="jampp-sticky-expand"
        padding={majorScale(2)}
        marginBottom={majorScale(3)}
        elevation={0}
        position="sticky"
        top="-24px"
        backgroundColor="white"
        zIndex="9"
        marginX={-1}
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between">
        <Pane>
          <Pane display="flex" alignItems="center" width="100%">
            <SearchInput
              placeholder="Search by title"
              maxWidth="75%"
              display="flex"
              alignItems="center"
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }} />
            <Button
              marginLeft={majorScale(1)}
              onClick={() => setShowFilters(!showFilters)}
              iconBefore={FilterListIcon}
              iconAfter={showFilters ? ChevronUpIcon : ChevronDownIcon}>
              {countActiveFilters() > 0 ? `${countActiveFilters()} active filters` : 'Search filters'}
            </Button>
          </Pane>
          {showFilters && (
            <Pane marginTop={minorScale(4)} display="flex" alignItems="center">
              <Pane marginLeft={majorScale(1)}>
                <StatusFilter
                  status={filterStatus}
                  setStatus={(v) => {
                    setFilterStatus(v);
                    searchParams.set('status', v);
                  }}
                  disabled={loading} />
              </Pane>
              <Pane marginLeft={majorScale(1)}>
                <TypeFilter
                  type={filterType}
                  setType={(v) => {
                    setFilterType(v);
                    searchParams.set('type', v);
                  }}
                  disabled={loading} />
              </Pane>
              <Pane marginLeft={majorScale(1)}>
                <UserFilter
                  prefix="Created by"
                  user={filterCreator}
                  setUser={(v) => {
                    setFilterCreator(v);
                    searchParams.set('created_by', v);
                  }}
                  disabled={loading} />
              </Pane>
              <Pane marginLeft={majorScale(1)}>
                <DateFilter
                  prefix="Created between"
                  disabled={loading}
                  defaultValue={filterCreatedRange !== 'all' ? filterCreatedRange : null}
                  onChange={(v) => {
                    setFilterCreatedRange(v);
                    searchParams.set('created_at', v);
                  }} />
              </Pane>
            </Pane>
          )}
        </Pane>
        <Pane>
          <OrderByMenu
            selected={orderBy}
            setSelected={(v) => {
              setOrderBy(v);
              searchParams.set('order_by', v);
            }} />
          <ColumnsMenu
            selectedColumns={selectedTableColumns}
            setSelectedColumns={setSelectedTableColumns} />
          <ExportTicketsDialog />
        </Pane>
      </Pane>
      <Pane className="requests-tables">
        <Table>
          <Table.Head height={36}>
            {columnShown('title') && <Table.TextHeaderCell>Title</Table.TextHeaderCell>}
            {columnShown('status') && <Table.TextHeaderCell>Status</Table.TextHeaderCell>}
            {columnShown('type') && <Table.TextHeaderCell>Type</Table.TextHeaderCell>}
            {columnShown('created_by') && <Table.TextHeaderCell>Created by</Table.TextHeaderCell>}
            {columnShown('updated_by') && <Table.TextHeaderCell>Updated by</Table.TextHeaderCell>}
            {columnShown('created_at') && <Table.TextHeaderCell>Created at</Table.TextHeaderCell>}
            {columnShown('updated_at') && <Table.TextHeaderCell>Updated at</Table.TextHeaderCell>}
            <Table.TextHeaderCell />
          </Table.Head>
          <Table.Body>
            {loading && (
              <Pane display="flex" alignItems="center" justifyContent="center" padding={majorScale(4)}>
                <Spinner />
              </Pane>
            )}

            {!loading && tickets.length < 1 && (
              <Pane display="flex" alignItems="center" justifyContent="center" padding={majorScale(4)}>
                <NoResults message="There are no tickets to show. Try changing your filter criteria" />
              </Pane>
            )}

            {!loading && tickets.length > 0 && (tickets.map((ticket) => (
              <Table.Row key={ticket.id}>
                {columnShown('title') && <Table.TextCell>{ticket.title}</Table.TextCell>}
                {columnShown('status') && <Table.TextCell><Badge color={getStatusColor(ticket.status)}>{ticket.status}</Badge></Table.TextCell>}
                {columnShown('type') && <Table.TextCell>{ticket.friendly_type}</Table.TextCell>}
                {columnShown('created_by') && <Table.TextCell><b>{ticket.created_by.name}</b></Table.TextCell>}
                {columnShown('updated_by') && <Table.TextCell><b>{ticket.updated_by ? ticket.updated_by.name : 'None'}</b></Table.TextCell>}
                {columnShown('created_at') && <Table.TextCell>{dayjs(ticket.created_at).format('DD-MM-YYYY')}</Table.TextCell>}
                {columnShown('updated_at') && <Table.TextCell>{dayjs(ticket.updated_at).format('DD-MM-YYYY')}</Table.TextCell>}
                <Table.TextCell className="actions-cell" display="grid" justifyContent="end">
                  {ticket.type === 'non_existent' ? (
                    <Heading size="100">Ticket type does not exist</Heading>
                  ) : (
                    <Link to={`/tickets/${ticket.id}`} is={ReactRouterLink}>
                      <IconButton
                        icon={ChevronRightIcon}
                        marginRight={majorScale(2)} />
                    </Link>
                  )}
                </Table.TextCell>
              </Table.Row>
            )))}
          </Table.Body>
        </Table>
      </Pane>
      <Pane marginTop={majorScale(2)}>
        <PaginationMenu
          pageMetadata={metadata}
          onPreviousPage={() => fetchData()}
          onNextPage={() => fetchData()}
          onChangePage={() => fetchData()}
          onItemsPerPageChange={() => fetchData()} />
      </Pane>
    </Pane>
  );
};

export default RequestsPage;
